
import router from '@/router';
import EazyHeading from '@/components/EazyHeading.vue';
import FileUploader from '@/components/Inputs/FileUploader.vue';
import requestAPI from '@/logic/requestBeApi';
import ImageCropper from '@/components/ImageCropper.vue';
import b64toBlob from '@/logic/b64toBlob';
import PhoneNumberInput from '@/components/Inputs/PhoneNumberInput.vue';
import formValidators from '@/logic/helpers/forms/validators';
import inputValidators from '@/logic/helpers/inputs/validators';
import CountryInput from '@/components/Inputs/CountryInput.vue';
import timezones from '@/logic/helpers/timezones/timezones';
import timezonesForCC from '@/resources/timezonesForCC';
import ServerResponseAlert from '@/components/ServerResponseAlert.vue';
import cvIframeSection from '@/components/CVIframeSection.vue';
import passDataToResponseAlert from '@/logic/helpers/api/passDataToResponseAlert';
import store from '@/store';

const getEmpytAddCandidateForm = function(): any {
  const empytAddCandidateForm = {
    client: '',
    cv_file: '',
    email: '',
    first_name: '',
    surname: '',
    phone: '',
    profile_picture: '',
    postal_code: '',
    skype: '',
    timezone: '',
    attachments: [] as unknown[],
  };

  return empytAddCandidateForm;
};

export default {
  name: 'ClientAddCandidate',

  components: {
    EazyHeading,
    FileUploader,
    ImageCropper,
    PhoneNumberInput,
    CountryInput,
    ServerResponseAlert,
    cvIframeSection,
  },

  data(): Record<string, unknown> {
    return {
      router,
      addCandidate: getEmpytAddCandidateForm(),
      candidateId: this.$route.query.candidateID,
      serverResponse: null,
      creatingOrUpdatingCandidate: false,
      cvParsed: false,
      parsingCV: false,
      errorPic: require('@/assets/no-image-error.png'),
      uploadNewCvForExistingCandidate: false,
      fetchingCandidate: false,
      fetchedNameAndSurnameForCvName: '',
      fetchedCv: '',
      fetchedProfilePic: '',
      imageToCrop: undefined,
      cropperActive: undefined,
      showPicPreview: false,
      formIsValid: false,
      enterDetailsManually: this.$route.query.fromMultipleUpload === 'true',
      shouldImmediatelyGoToNextCandidateAfterUpload:
        this.$route.query.fromMultipleUpload === 'true',
      clients: [],
      client: '',
    };
  },

  computed: {
    roleType(): string {
      return localStorage.getItem('roleType');
    },

    fromMultipleUpload(): boolean {
      return this.$route.query.fromMultipleUpload === 'true';
    },

    fromNewProject(): boolean {
      return this.$route.query.from_new_project === 'true';
    },

    validationRules(): Record<string, unknown> {
      return {
        client: [{ validator: inputValidators.primitive, trigger: 'change' }],
        cv_file: [{ validator: inputValidators.primitive, trigger: 'change' }],
        email: [{ validator: inputValidators.primitive, trigger: 'change' }],
        first_name: [{ validator: inputValidators.primitive, trigger: 'change' }],
        surname: [{ validator: inputValidators.primitive, trigger: 'change' }],
        timezone: [{ validator: inputValidators.primitive, trigger: 'change' }],
        phone: [
          {
            validator: inputValidators.primitive,
            trigger: this.cvParsed || this.fetchedCv ? 'change' : 'blur',
          },
        ],
      };
    },

    showCvUploader(): boolean {
      return !this.candidateId || !this.fetchedCv;
    },

    showCandidateForm(): boolean {
      return this.cvParsed || this.candidateId || this.enterDetailsManually;
    },

    renderShowCandidateFormButton(): boolean {
      return !this.cvParsed && !this.candidateId && !this.enterDetailsManually;
    },

    picPreview(): HTMLImageElement {
      const d: any | Document = document;

      return d.picPreview as HTMLImageElement;
    },
  },

  mounted(): void {
    if (this.candidateId) this.getCandidate();
    if (this.fromMultipleUpload) {
      const currentlySelectedCV = store.state.multipleCVs.currentlySelectedCV;
      this.parseCandData(currentlySelectedCV);
      this.runUploadPictureLogic(currentlySelectedCV);
    }
    this.getClients();
  },

  methods: {
    async getClients(): Promise<void> {
      let url = '/api/v1/favourites/clients';
      const resp = await requestAPI({ url, method: 'GET' });
      if (resp.ok) {
        const data = await resp.json();
        this.clients = data
          .filter((client) => client.contact_details && client.contact_details.first_name)
          .map((client) => {
            return {
              ...client,
              profile_image: undefined,
              first_name: client.contact_details.first_name,
              surname: client.contact_details.surname,
            };
          });
      } else if (resp.status !== 401) {
        this.$message.error(this.$t('someErrorOccured'));
      }
    },

    getClientFromUI(): string[] {
      const clientId = this.addCandidate.client;
      return clientId ? [`/api/v1/clients/${clientId}`] : [];
    },

    runUploadPictureLogic(parsedCV) {
      if (
        !this.fetchedProfilePic &&
        parsedCV.profile_image &&
        !parsedCV.profile_image.includes('///')
      ) {
        this.handleProfilePicUpload(parsedCV.profile_image);

        const picName =
          !this.addCandidate.first_name || !this.addCandidate.surname
            ? `${this.$t('candidate')}.png`
            : `${this.addCandidate.first_name}_${this.addCandidate.surname}.png`;
        document.querySelector('.pic-name').innerHTML =
          parsedCV.profile_image && this.cropperActive ? picName : this.$t('upload');
      }
    },

    handleProfilePicUpload(payload: string): void {
      const file = b64toBlob(payload, 'image');

      if (file.size >= 2_000_000) {
        this.$message({
          showClose: true,
          message: this.$t('imageTooBig'),
          type: 'warning',
          duration: 10000,
        });

        return;
      }

      this.imageToCrop = URL.createObjectURL(file);
      this.cropperActive = true;
    },

    getCroppedImage(payload: unknown): void {
      this.addCandidate.profile_picture = payload;
      this.cropperActive = false;

      setTimeout(() => {
        this.picPreview.src = `data:image;base64,${this.addCandidate.profile_picture}`;
      });
    },

    async getCandidate(): Promise<void> {
      this.fetchingCandidate = true;

      const resp = await requestAPI({
        url: `/api/v1/candidates/${this.candidateId}`,
        method: 'GET',
      });

      if (resp.ok) {
        const data = await resp.json();
        this.parseCandData(data);
      } else if (resp.status !== 401) {
        this.$message.error(this.$t('someErrorOccurred'));
        router.go(-1);
      }

      this.fetchingCandidate = false;
    },

    parseCandData(data: {
      first_name: string;
      surname: string;
      email: string;
      phone: string;
      skype: string;
      postal_code: string;
      profile_picture: { url: string };
      cv_file: any;
      timezone: string;
    }): void {
      this.fetchedNameAndSurnameForCvName = `${data.first_name} ${data.surname}_${this.$t(
        'resume',
      )}`.replaceAll(' ', '_');

      this.addCandidate.email = data.email;
      this.addCandidate.first_name = data.first_name;
      this.addCandidate.surname = data.surname;
      this.addCandidate.phone = data.phone;
      this.addCandidate.postal_code = data.postal_code;
      this.addCandidate.skype = data.skype;
      this.addCandidate.timezone = this.getCountryForTimezone(data.timezone);
      if (this.fromMultipleUpload) this.addCandidate.cv_file = data.cv_file;
      this.fetchedProfilePic = data.profile_picture?.url;
      this.fetchedCv = data.cv_file?.url;
      setTimeout(() => {
        this.picPreview.src = data.profile_picture?.url;
      });
    },

    getCountryForTimezone(candidateTimezone: string | undefined): string {
      const timezone = candidateTimezone ? candidateTimezone : this.getClientsTimezone();
      const countryCode = Object.keys(timezonesForCC).find((CC) => {
        return timezonesForCC[CC].includes(timezone);
      });

      return countryCode;
    },

    getClientsTimezone(): string {
      return timezones.getUserTimezoneFromBrowser();
    },

    async handleFileUpload(payload: unknown): Promise<void> {
      this.addCandidate.cv_file = payload;
      this.parsingCV = true;

      const resp = await requestAPI({
        url: `/api/v1/cvs/parse`,
        method: 'POST',
        payload: {
          files: [
            {
              file: this.addCandidate.cv_file,
            },
          ],
        },
      });

      if (resp.ok) {
        this.$message.success(this.$t('cvParsed'));

        const data = await resp.json();
        const parsedCV = data.cvs[0];

        this.addCandidate.email = parsedCV.email;
        this.addCandidate.first_name = parsedCV.name;
        this.addCandidate.surname = parsedCV.surname;
        this.addCandidate.phone = parsedCV.phone;

        this.runUploadPictureLogic(parsedCV);

        this.highlightAutofilledInputs();
      } else if (resp.status !== 401) {
        this.$message({
          type: 'warning',
          message: this.$t('cvParsingFailed'),
          duration: 7000,
        });
      }

      this.cvParsed = true;
      this.parsingCV = false;

      setTimeout(() => {
        if (!this.addCandidate.profile_picture) return;

        this.picPreview.src = `data:image;base64,${this.addCandidate.profile_picture}`;
      });
    },

    highlightAutofilledInputs(): void {
      const dataToFill = [
        { data: this.addCandidate.email, id: '#email' },
        { data: this.addCandidate.first_name, id: '#firstName' },
        { data: this.addCandidate.surname, id: '#surname' },
        { data: this.addCandidate.phone, id: '.selenium--phone-input' },
        { data: this.addCandidate.profile_picture, id: '#profilePic' },
      ];

      dataToFill.forEach((input) => {
        if (input.data) {
          let elementToHighlight: HTMLElement;

          if (input.id === '#profilePic' && !this.fetchedProfilePic) {
            elementToHighlight = document
              .querySelector(input.id)
              .querySelector('.file-uploader__btn').firstElementChild.children[1] as HTMLElement;
          } else elementToHighlight = document.querySelector(input.id);

          setTimeout(() => {
            elementToHighlight.style.backgroundColor = '#e4ece7';
            elementToHighlight.style.backgroundImage =
              'radial-gradient(circle, #e4ece7 0%, #f3fff8 100%)';

            setTimeout(() => {
              elementToHighlight.style.backgroundColor = '#f3fff8';
              elementToHighlight.style.backgroundImage = 'none';
            }, 5000);
          }, 500);
        }
      });
    },

    returnToPreviousView(candToAutoSelect = ''): void {
      if (candToAutoSelect) localStorage.setItem(candToAutoSelect, 'recentlyAddedCand');

      router.go(-1);
    },

    getCandidatePayload(): Record<string, unknown> {
      let payload = JSON.parse(JSON.stringify(this.addCandidate));
      payload.timezone = timezonesForCC[payload.timezone][0];
      if (this.roleType === 'assessorPlus') {
        if (this.fromNewProject) {
          this.addCandidate.client = localStorage.getItem('clientIdFromProject');
        }
        const clientRoute = this.getClientFromUI()[0];
        if (clientRoute) {
          payload.client = clientRoute;
        }
      }
      return payload;
    },

    async createOrUpdateCandidate(): Promise<void> {
      formValidators.validateForm(this, 'addCandidateForm');

      if (!this.formIsValid) return;

      this.serverResponse = {};
      this.creatingOrUpdatingCandidate = true;

      const payload: Record<string, unknown> = this.getCandidatePayload();

      Object.entries(this.addCandidate).forEach((entry) =>
        !entry[1] ? delete payload[entry[0]] : null,
      );

      let resp: Response;

      if (this.candidateId) {
        resp = await requestAPI({
          url: `/api/v1/candidates/${this.candidateId}`,
          contentType: 'application/merge-patch+json',
          method: 'PATCH',
          payload,
        });
      } else {
        resp = await requestAPI({
          url: '/api/v1/candidates',
          method: 'POST',
          payload,
        });
      }

      if (resp.ok) {
        const data = await resp.json();

        this.$message.success(
          this.candidateId ? this.$t('candidateDataUpdated') : this.$t('candidateAdded'),
        );

        if (this.fromMultipleUpload) this.updateCVsListWithUploadedStatus();
        this.makeDecisionWhereToGoAfterSuccessfullUpload(data);
      } else if (resp.status !== 401) {
        const data = await passDataToResponseAlert(resp);
        this.serverResponse = data;
      }

      this.creatingOrUpdatingCandidate = false;
    },

    updateCVsListWithUploadedStatus() {
      store.commit(
        'multipleCVs/updateCVsListWithUploadedStatus',
        store.state.multipleCVs.currentlySelectedCV.id,
      );
    },

    makeDecisionWhereToGoAfterSuccessfullUpload(data: any): void {
      setTimeout(() => {
        if (this.shouldImmediatelyGoToNextCandidateAfterUpload) {
          const aheadOfCurrentlyselectedCandidateIdStillExistsCandidatesToUpload =
            store.state.multipleCVs.parsedCVs.filter((cv) => {
              const currentlySelectedCVId = store.state.multipleCVs.currentlySelectedCV.id;
              const lowestNewIdToConsider = cv.id >= currentlySelectedCVId + 1;
              return !cv.candidateUploaded && lowestNewIdToConsider;
            }).length >= 1;
          aheadOfCurrentlyselectedCandidateIdStillExistsCandidatesToUpload
            ? this.immediatelyGoToNextCandidate()
            : this.redirectUserDependingOnRoleType(this.roleType);
        } else {
          this.fromMultipleUpload
            ? this.redirectUserDependingOnRoleType(this.roleType)
            : this.returnToPreviousView(this.candidateId ? null : data.id);
        }
      }, 800);
    },

    redirectUserDependingOnRoleType(roleType: string): void {
      const route =
        roleType === 'assessorPlus' ? '/assessor-add-candidates' : '/client-add-candidates';
      router.push(route);
    },

    immediatelyGoToNextCandidate(): void {
      this.fetchingCandidate = true;
      store.commit(
        'multipleCVs/setCurrentlySelectedCVToNextNotUploaded',
        store.state.multipleCVs.currentlySelectedCV.id,
      );
      this.addCandidate = getEmpytAddCandidateForm();

      setTimeout(() => {
        const currentlySelectedCV = store.state.multipleCVs.currentlySelectedCV;
        this.parseCandData(currentlySelectedCV);
        this.runUploadPictureLogic(currentlySelectedCV);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => {
          this.fetchingCandidate = false;
        }, 300);
      }, 100);
    },

    deleteProfilePic(): void {
      this.addCandidate.profile_picture = '';
      this.fetchedProfilePic = '';

      setTimeout(() => {
        document.querySelector('.pic-name').innerHTML = this.$t('upload');
      });
    },

    handlePicError(): void {
      this.picPreview.src = this.errorPic;
    },

    triggerUpload(): void {
      this.cropperActive = false;

      (document.querySelector('#picUploader') as HTMLElement).click();
    },

    goToPreviousPage() {
      this.fromMultipleUpload ? router.push('/client-add-candidates') : router.go(-1);
    },
  },
};
